import { Link } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { ArrowRight, Code, Cloud, Wrench, MonitorSmartphone, Database, Shield, GitBranch, Layers, Cpu, Rocket, BarChart3, Lock } from "lucide-react";

const mainServices = [
  {
    icon: Code,
    title: "Custom Software Development",
    description: "We design and develop tailor-made software solutions that align perfectly with your business goals. From single-page applications to complex enterprise platforms, our team leverages modern frameworks and best practices to deliver robust, scalable software.",
    features: [
      "Full-stack web application development",
      "RESTful API design and development",
      "Progressive Web Apps (PWA)",
      "E-commerce platform development",
      "Enterprise resource planning (ERP) systems",
      "SaaS product development",
    ],
    tech: ["React", "Next.js", "Node.js", "Python", "FastAPI", "PostgreSQL", "MongoDB"],
    accent: "indigo",
  },
  {
    icon: Cloud,
    title: "Application Hosting",
    description: "Reliable, secure, and high-performance hosting solutions for your applications. We manage the infrastructure so you can focus on your business. Our hosting environments are optimized for speed, security, and uptime.",
    features: [
      "Cloud infrastructure setup and management",
      "Auto-scaling and load balancing",
      "SSL certificate management",
      "Automated backup and disaster recovery",
      "CDN integration for global performance",
      "99.9% uptime SLA guarantee",
    ],
    tech: ["AWS", "Google Cloud", "Docker", "Kubernetes", "Nginx", "CloudFlare"],
    accent: "cyan",
  },
  {
    icon: Wrench,
    title: "Maintenance & Support",
    description: "Keep your applications running at peak performance with our comprehensive maintenance and support services. We proactively monitor, update, and optimize your systems to prevent issues before they impact your users.",
    features: [
      "24/7 application monitoring and alerting",
      "Performance optimization and tuning",
      "Security patches and vulnerability fixes",
      "Bug fixes and issue resolution",
      "Feature enhancements and updates",
      "Regular health checks and reporting",
    ],
    tech: ["Grafana", "Prometheus", "Sentry", "New Relic", "PagerDuty"],
    accent: "indigo",
  },
];

const additionalServices = [
  { icon: MonitorSmartphone, title: "UI/UX Design", desc: "User-centered design that drives engagement and conversion." },
  { icon: Database, title: "Database Architecture", desc: "Optimized data structures for performance and scalability." },
  { icon: Shield, title: "Security Audits", desc: "Comprehensive security assessments and hardening." },
  { icon: GitBranch, title: "DevOps & CI/CD", desc: "Automated pipelines for faster, reliable deployments." },
  { icon: Layers, title: "API Integration", desc: "Seamless third-party service connections and data flow." },
  { icon: Cpu, title: "Performance Optimization", desc: "Speed improvements across frontend and backend." },
  { icon: BarChart3, title: "Analytics Setup", desc: "Data tracking and business intelligence dashboards." },
  { icon: Lock, title: "Compliance", desc: "GDPR, HIPAA, and industry-specific compliance solutions." },
];

const process = [
  { step: "01", title: "Discovery", desc: "We analyze your requirements, understand your business, and define the project scope." },
  { step: "02", title: "Design", desc: "Our designers create wireframes and prototypes, ensuring the solution meets user needs." },
  { step: "03", title: "Development", desc: "Agile sprints with regular demos. You see progress every week." },
  { step: "04", title: "Testing", desc: "Rigorous QA processes including unit, integration, and user acceptance testing." },
  { step: "05", title: "Deployment", desc: "Smooth deployment with zero-downtime strategies and rollback plans." },
  { step: "06", title: "Support", desc: "Ongoing maintenance, monitoring, and iterative improvements post-launch." },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10 text-xs font-mono" data-testid="services-hero-badge">
              OUR SERVICES
            </Badge>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 leading-[1.1] tracking-tight">
              End-to-end software{" "}
              <span className="gradient-text">solutions.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-400 leading-relaxed max-w-xl">
              From development to deployment and beyond — we cover every stage 
              of your software lifecycle with expertise built across 20+ web applications.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="relative py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {mainServices.map((service, i) => (
              <div
                key={i}
                data-testid={`main-service-${i}`}
                className={`grid lg:grid-cols-2 gap-12 items-start ${i % 2 === 1 ? "lg:direction-rtl" : ""}`}
              >
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                    service.accent === "cyan" ? "bg-cyan-500/10 text-cyan-400" : "bg-indigo-500/10 text-indigo-400"
                  }`}>
                    <service.icon className="w-7 h-7" />
                  </div>
                  <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-50 tracking-tight mb-4">
                    {service.title}
                  </h2>
                  <p className="text-slate-400 text-sm leading-relaxed mb-8">
                    {service.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {service.tech.map((t) => (
                      <span key={t} className="px-3 py-1 rounded-full bg-slate-800/80 text-slate-300 text-xs font-mono border border-slate-700/50">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className={`bg-slate-900/50 border border-slate-800 rounded-2xl p-8 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <h3 className="font-['Outfit'] font-semibold text-slate-200 mb-6 text-sm uppercase tracking-wider">
                    What's Included
                  </h3>
                  <ul className="space-y-4">
                    {service.features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-3">
                        <Rocket className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          service.accent === "cyan" ? "text-cyan-400" : "text-indigo-400"
                        }`} />
                        <span className="text-slate-300 text-sm">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services Grid */}
      <section className="relative py-24 lg:py-32 bg-slate-900/30" data-testid="additional-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
              And <span className="gradient-text">more.</span>
            </h2>
            <p className="mt-4 text-base text-slate-400">Specialized services to complement your core needs.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalServices.map((s, i) => (
              <div key={i} data-testid={`additional-service-${i}`} className="p-6 rounded-xl bg-slate-900/80 border border-slate-800 hover:bg-slate-800/80 hover:border-indigo-500/20 transition-all group">
                <s.icon className="w-6 h-6 text-indigo-400 mb-4 group-hover:text-indigo-300 transition-colors" />
                <h3 className="font-['Outfit'] font-semibold text-slate-200 text-sm mb-2">{s.title}</h3>
                <p className="text-slate-400 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="relative py-24 lg:py-32" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border-cyan-500/20 hover:bg-cyan-500/10 text-xs font-mono">
              OUR PROCESS
            </Badge>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
              How we <span className="gradient-text">deliver.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {process.map((p, i) => (
              <div key={i} data-testid={`process-step-${i}`} className="p-6 rounded-xl bg-slate-900/50 border border-slate-800 relative group hover:border-indigo-500/20 transition-all">
                <span className="font-['JetBrains_Mono'] text-4xl font-bold text-indigo-500/10 absolute top-4 right-6 group-hover:text-indigo-500/20 transition-colors">
                  {p.step}
                </span>
                <h3 className="font-['Outfit'] font-semibold text-slate-200 mb-2">{p.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-t from-indigo-600/5 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
            Let's build your next <span className="gradient-text">big thing.</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 max-w-lg mx-auto">
            Share your project requirements and we'll get back with a tailored proposal.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta"
            className="mt-8 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:-translate-y-0.5"
          >
            Get a Free Consultation
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
